import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { CTABand, ParallaxImage, Reveal, SectionDivider, ShareButton } from "../components/Chrome";

const EASE = [0.22, 1, 0.36, 1];

export default function CaseStudies() {
  const { data } = useQuery({
    queryKey: ["content", "case-studies"],
    queryFn: async () => (await api.get("/content/case-studies")).data,
  });
  const c = data?.data;
  usePageMeta("Case Studies — Klyx™", c?.hero?.sub);
  if (!c) return <div data-testid="case-studies-loading" className="min-h-screen bg-ink" />;

  return (
    <div data-testid="case-studies-page">
      <section className="pt-40 pb-20 lg:pt-52 spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light mb-6"
          >
            {c.hero.eyebrow}
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "105%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.15, ease: EASE }}
              data-testid="case-studies-title"
              className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-silver"
            >
              {c.hero.title}
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-8 text-base sm:text-lg text-zinc-200 leading-relaxed max-w-xl"
          >
            {c.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.7 }} className="mt-8">
            <ShareButton title="Case Studies — Klyx™" testId="case-studies-share-button" />
          </motion.div>
        </div>
      </section>

      {c.studies?.map((s, i) => (
        <section key={`${s.app}-${i}`} data-testid={`case-study-${i}`} className={i % 2 === 1 ? "bg-elevated" : "bg-surface"}>
          <SectionDivider />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}>
              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <ParallaxImage src={s.image} alt={s.title} className="w-full h-full" speed={0.08} />
              </div>
            </Reveal>
            <Reveal delay={0.1} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
              <div className="flex items-center gap-4 mb-5">
                <span className="h-[2px] w-10 bg-mauve shrink-0" />
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve-light">
                  Case {String(i + 1).padStart(2, "0")} — {s.app}
                </p>
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-silver text-balance">{s.title}</h2>
              <p className="mt-6 text-base sm:text-lg text-zinc-200 leading-relaxed">{s.summary}</p>
              {s.results && (
                <ul className="mt-8 space-y-4">
                  {s.results.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm sm:text-base text-zinc-300">
                      <Plus size={15} className="text-mauve-light mt-0.5 shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          </div>
        </section>
      ))}

      <CTABand cta={c.cta} />
    </div>
  );
}
